import { FC, FormEvent, useState } from 'react';

import { router, useForm, usePage } from '@inertiajs/react';
import { clsx } from 'clsx';
import { Eye, EyeOff, Save, ChevronDown, ChevronUp } from 'lucide-react';

import { Button } from '../../components/ui/Button';
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
    CardDescription,
} from '../../components/ui/Card';
import { Input } from '../../components/ui/Input';
import { Label } from '../../components/ui/Label';
import { Routes } from '../../models/routes';

interface Props {
    isOpen: boolean;
    onToggle: () => void;
}

type FormValues = {
    current_password: string;
    password: string;
    password_confirmation: string;
};

type PageProps = {
    errors: Partial<Record<keyof FormValues, string>> | null;
};

export const PasswordChangeForm: FC<Props> = ({ isOpen, onToggle }) => {
    const { errors } = usePage<PageProps>().props;

    const { data, setData, processing, reset } = useForm<FormValues>({
        current_password: '',
        password: '',
        password_confirmation: '',
    });

    const [showPasswords, setShowPasswords] = useState({
        current: false,
        new: false,
        confirmation: false,
    });

    const handleSubmit = (e: FormEvent) => {
        e.preventDefault();
        router.patch(
            Routes.ProfileEdit,
            { user: data },
            {
                preserveScroll: true,
                onSuccess: () => {
                    reset();
                    setShowPasswords({
                        current: false,
                        new: false,
                        confirmation: false,
                    });
                },
            },
        );
    };

    const togglePasswordVisibility = (
        field: 'current' | 'new' | 'confirmation',
    ) => {
        setShowPasswords((prev) => ({
            ...prev,
            [field]: !prev[field],
        }));
    };

    return (
        <Card className="h-fit">
            <CardHeader
                className="cursor-pointer transition-colors hover:bg-muted/50"
                onClick={onToggle}
            >
                <CardTitle className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <Eye className="h-5 w-5 text-primary" />
                        Зміна пароля
                    </div>
                    {isOpen ? (
                        <ChevronUp className="h-4 w-4 text-muted-foreground" />
                    ) : (
                        <ChevronDown className="h-4 w-4 text-muted-foreground" />
                    )}
                </CardTitle>
                <CardDescription>
                    Оновіть свій пароль для безпеки
                </CardDescription>
            </CardHeader>
            {isOpen && (
                <CardContent>
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="current_password">
                                Поточний пароль
                            </Label>
                            <div className="relative">
                                <Input
                                    id="current_password"
                                    type={
                                        showPasswords.current
                                            ? 'text'
                                            : 'password'
                                    }
                                    value={data.current_password}
                                    onChange={(e) =>
                                        setData(
                                            'current_password',
                                            e.target.value,
                                        )
                                    }
                                    className={clsx({
                                        'border-red-600':
                                            errors?.current_password,
                                    })}
                                    placeholder="Введіть поточний пароль"
                                    autoComplete="current-password"
                                />
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    className="absolute top-0 right-0 h-full px-3 py-2 hover:bg-transparent"
                                    onClick={() =>
                                        togglePasswordVisibility('current')
                                    }
                                >
                                    {showPasswords.current ? (
                                        <EyeOff className="h-4 w-4" />
                                    ) : (
                                        <Eye className="h-4 w-4" />
                                    )}
                                </Button>
                            </div>
                            {errors?.current_password && (
                                <p className="text-sm text-red-600">
                                    {errors.current_password}
                                </p>
                            )}
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="password">Новий пароль</Label>
                            <div className="relative">
                                <Input
                                    id="password"
                                    type={
                                        showPasswords.new ? 'text' : 'password'
                                    }
                                    value={data.password}
                                    onChange={(e) =>
                                        setData('password', e.target.value)
                                    }
                                    className={clsx({
                                        'border-red-600': errors?.password,
                                    })}
                                    placeholder="Введіть новий пароль"
                                    autoComplete="new-password"
                                />
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    className="absolute top-0 right-0 h-full px-3 py-2 hover:bg-transparent"
                                    onClick={() =>
                                        togglePasswordVisibility('new')
                                    }
                                >
                                    {showPasswords.new ? (
                                        <EyeOff className="h-4 w-4" />
                                    ) : (
                                        <Eye className="h-4 w-4" />
                                    )}
                                </Button>
                            </div>
                            {errors?.password && (
                                <p className="text-sm text-red-600">
                                    {errors.password}
                                </p>
                            )}
                        </div>

                        <div className="space-y-2">
                            <Label htmlFor="password_confirmation">
                                Підтвердження пароля
                            </Label>
                            <div className="relative">
                                <Input
                                    id="password_confirmation"
                                    type={
                                        showPasswords.confirmation
                                            ? 'text'
                                            : 'password'
                                    }
                                    value={data.password_confirmation}
                                    onChange={(e) =>
                                        setData(
                                            'password_confirmation',
                                            e.target.value,
                                        )
                                    }
                                    className={clsx({
                                        'border-red-600':
                                            errors?.password_confirmation,
                                    })}
                                    placeholder="Підтвердіть новий пароль"
                                    autoComplete="new-password"
                                />
                                <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    className="absolute top-0 right-0 h-full px-3 py-2 hover:bg-transparent"
                                    onClick={() =>
                                        togglePasswordVisibility('confirmation')
                                    }
                                >
                                    {showPasswords.confirmation ? (
                                        <EyeOff className="h-4 w-4" />
                                    ) : (
                                        <Eye className="h-4 w-4" />
                                    )}
                                </Button>
                            </div>
                            {errors?.password_confirmation && (
                                <p className="text-sm text-red-600">
                                    {errors.password_confirmation}
                                </p>
                            )}
                        </div>

                        <Button
                            type="submit"
                            className="w-full"
                            variant="outline"
                            disabled={processing}
                        >
                            <Save className="mr-2 h-4 w-4" />
                            {processing
                                ? 'Змінення пароля...'
                                : 'Змінити пароль'}
                        </Button>
                    </form>
                </CardContent>
            )}
        </Card>
    );
};
